import type { EnemyKind } from "./types";

export type ChallengeType = "horde" | "elite" | null;

export interface WaveSpawnProfile {
  readonly openingDelayTicks: number;
  readonly intervalStartTicks: number;
  readonly intervalEndTicks: number;
  readonly groupSize: number;
  readonly lateGroupBonus: number;
  readonly enemyPool: readonly EnemyKind[];
  readonly lateEnemyPool: readonly EnemyKind[];
}

const WAVE_SPAWN_PROFILES: readonly WaveSpawnProfile[] = [
  profile(30, 44, 34, 1, 1, ["mite", "crumb"], ["mite", "crumb"]),
  profile(28, 42, 32, 1, 1, ["mite", "crumb", "scuttler"], ["mite", "scuttler"]),
  profile(27, 40, 30, 2, 0, ["mite", "scuttler", "stinger"], ["scuttler", "stinger"]),
  profile(26, 38, 28, 2, 1, ["mite", "scuttler", "stinger"], ["stinger", "roller"]),
  profile(25, 37, 27, 2, 1, ["mite", "scuttler", "stinger", "roller"], ["scuttler", "roller"]),
  profile(24, 36, 26, 2, 1, ["mite", "scuttler", "stinger", "roller"], ["stinger", "roller"]),
  profile(23, 35, 25, 2, 1, ["mite", "scuttler", "stinger", "roller"], ["scuttler", "stinger", "roller"]),
  profile(22, 34, 24, 2, 1, ["mite", "scuttler", "stinger", "roller", "brute"], ["stinger", "roller", "brute"]),
  profile(21, 33, 23, 2, 2, ["mite", "scuttler", "stinger", "roller", "brute"], ["scuttler", "roller", "brute"]),
  profile(20, 32, 22, 2, 2, ["scuttler", "stinger", "roller", "brute"], ["stinger", "roller", "brute"]),
  profile(20, 31, 21, 2, 2, ["scuttler", "stinger", "roller", "brute", "moth"], ["stinger", "brute", "moth"]),
  profile(19, 30, 20, 3, 1, ["scuttler", "stinger", "roller", "brute", "moth"], ["roller", "brute", "moth"]),
  profile(19, 29, 19, 3, 1, ["stinger", "roller", "brute", "moth", "shellback"], ["brute", "moth", "shellback"]),
  profile(18, 28, 18, 3, 2, ["stinger", "roller", "brute", "moth", "shellback", "slime"], ["moth", "shellback", "slime"]),
  profile(18, 27, 17, 3, 2, ["roller", "brute", "moth", "shellback", "slime"], ["brute", "shellback", "slime"]),
  profile(17, 26, 16, 3, 2, ["roller", "brute", "moth", "shellback", "slime"], ["moth", "shellback", "slime"]),
  profile(17, 25, 15, 3, 2, ["brute", "moth", "shellback", "slime"], ["moth", "shellback", "slime"]),
  profile(16, 24, 14, 3, 3, ["brute", "moth", "shellback", "slime", "charger"], ["shellback", "slime", "charger"]),
  profile(16, 23, 13, 4, 2, ["brute", "moth", "shellback", "slime", "charger"], ["moth", "shellback", "charger"]),
  profile(15, 22, 12, 4, 2, ["moth", "shellback", "slime", "charger"], ["shellback", "slime", "charger"]),
] as const;

const ENEMY_WAVE_SCALING: Readonly<
  Record<EnemyKind, { healthPerWave: number; damagePerWave: number }>
> = {
  crumb: { healthPerWave: 3, damagePerWave: 0.35 },
  mite: { healthPerWave: 5, damagePerWave: 0.6 },
  scuttler: { healthPerWave: 4, damagePerWave: 0.55 },
  brute: { healthPerWave: 12, damagePerWave: 0.85 },
  roller: { healthPerWave: 8, damagePerWave: 0.7 },
  stinger: { healthPerWave: 5, damagePerWave: 0.75 },
  slime: { healthPerWave: 14, damagePerWave: 0.8 },
  moth: { healthPerWave: 8, damagePerWave: 0.65 },
  charger: { healthPerWave: 18, damagePerWave: 1.1 },
  shellback: { healthPerWave: 28, damagePerWave: 1.2 },
  "red-captain": { healthPerWave: 40, damagePerWave: 1.4 },
  "void-cook": { healthPerWave: 55, damagePerWave: 1.6 },
  "iron-maw": { healthPerWave: 70, damagePerWave: 1.8 },
  "jam-queen": { healthPerWave: 0, damagePerWave: 0 },
  "clockwork-chef": { healthPerWave: 0, damagePerWave: 0 },
  "the-breakfast": { healthPerWave: 140, damagePerWave: 2.2 },
  tree: { healthPerWave: 5, damagePerWave: 0 },
};

export function waveDurationSeconds(wave: number): number {
  if (wave === 20) return 90;
  if (wave > 20) return 60;
  return Math.min(60, 20 + Math.max(0, wave - 1) * 5);
}

export function getWaveSpawnProfile(wave: number): WaveSpawnProfile {
  if (wave <= 1) return WAVE_SPAWN_PROFILES[0]!;
  if (wave <= WAVE_SPAWN_PROFILES.length) {
    return WAVE_SPAWN_PROFILES[wave - 1]!;
  }
  const base = WAVE_SPAWN_PROFILES.at(-1)!;
  const endlessDepth = Math.min(10, Math.floor((wave - 20) / 3));
  return {
    ...base,
    intervalStartTicks: Math.max(10, base.intervalStartTicks - endlessDepth),
    intervalEndTicks: Math.max(6, base.intervalEndTicks - endlessDepth),
    groupSize: Math.min(8, base.groupSize + Math.floor(endlessDepth / 2)),
  };
}

export function challengeTypeForWave(
  seed: number,
  dangerLevel: number,
  wave: number,
): ChallengeType {
  if (dangerLevel < 2 || wave < 11 || wave > 18) return null;
  const slots = dangerLevel >= 4 ? 3 : 1;
  const wavePairs = [11, 14, 17] as const;
  for (let slot = 0; slot < slots; slot += 1) {
    const hash = seededHash(seed, slot + 1);
    const challengeWave = wavePairs[slot]! + (hash & 1);
    if (wave !== challengeWave) continue;
    if (slot === 2) return "elite";
    return ((hash >>> 1) % 10) < 4 ? "horde" : "elite";
  }
  return null;
}

export function enemyWaveScaling(kind: EnemyKind): {
  healthPerWave: number;
  damagePerWave: number;
} {
  return ENEMY_WAVE_SCALING[kind];
}

function profile(
  openingDelayTicks: number,
  intervalStartTicks: number,
  intervalEndTicks: number,
  groupSize: number,
  lateGroupBonus: number,
  enemyPool: readonly EnemyKind[],
  lateEnemyPool: readonly EnemyKind[],
): WaveSpawnProfile {
  return {
    openingDelayTicks,
    intervalStartTicks,
    intervalEndTicks,
    groupSize,
    lateGroupBonus,
    enemyPool,
    lateEnemyPool,
  };
}

function seededHash(seed: number, salt: number): number {
  let value = (seed ^ Math.imul(salt, 0x9e3779b9)) >>> 0;
  value ^= value >>> 16;
  value = Math.imul(value, 0x7feb352d);
  value ^= value >>> 15;
  value = Math.imul(value, 0x846ca68b);
  return (value ^ (value >>> 16)) >>> 0;
}
